import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Drawer, Pagination, Select, message } from 'antd';
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  CircleDollarSign,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  LayoutDashboard,
  Loader2,
  Scale,
  School,
  Search,
  Users,
} from 'lucide-react';
import { Button, Input, PageHeader } from '@/shared/components';
import { ApiError } from '@/shared/api';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import {
  DashboardSettingSourceNotes,
} from '@/features/settings/components/DashboardSettingSourceNote';
import {
  useTeacherEquitySpreadSeverityHighAmountResolved,
  useTeacherEquitySpreadSeverityHighRatioResolved,
  useTeacherEquitySpreadSeverityVeryHighAmountResolved,
  useTeacherEquitySpreadSeverityVeryHighRatioResolved,
} from '@/features/settings/hooks/useResolvedDashboardSetting';
import { useTeacherEquityNationalKpisQuery } from '@/features/reports/hooks/useTeacherEquityNationalKpisQuery';
import { useBudgetTeacherPartialInfoQuery } from '@/features/reports/hooks/useBudgetTeacherPartialInfoQuery';
import { usePickListQuery } from '@/features/reports/hooks/usePickListQuery';
import type { TeacherEquitySchoolSpreadDto } from '@/features/reports/api';
import { PICK_LIST_CODES } from '@/features/reports/api';
import {
  budgetPickEducationTitles,
  budgetPickFirstTitle,
  filterBudgetTeachersByAppliedPickLists,
  fmtBudgetDdMmYyyy,
  fmtBudgetLumpSum,
  fmtBudgetTotalSalaryLl,
  fmtBudgetTotalSalaryUsd,
  sumBudgetTeacherSalaryTotalUsd,
  teacherRowsFromBudgetPartial,
} from '@/features/reports/utils/budgetTeacherPartialDisplay';
import {
  downloadStringMatrixAsPdf,
  downloadStringMatrixAsXlsx,
} from '@/features/reports/utils/teacherEquityAlertExports';
import {
  PEER_VARIANCE_DIMENSIONS_LABEL,
  spreadSeverityLabel,
  type EmploymentType,
  type ExecutiveBelowPeerRow,
  type ExecutiveTopSpreadRow,
  type SpreadSeverityLevel,
} from '@/features/reports/data/teacherEquityMock';

const SCHOOL_ALERTS_PAGE_SIZE = 15;

const BUDGET_TEACHER_DRAWER_PAGE_DEFAULT = 12;
const BUDGET_TEACHER_DRAWER_PAGE_SIZES = [10, 15, 25, 50] as const;

/** Maps academic year filter (e.g. 2025–2026) to API year for teacher-equity-report. */
function reportingYearDigitsFromAcademicYearId(academicYearId: string): string | undefined {
  const m = academicYearId.match(/^(\d{4})/);
  return m?.[1];
}

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtMoneyOrDash(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return fmtMoney(n);
}

function fmtPctSigned(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}

/**
 * `schoolSpreads` often sends ±100% when peer/school medians are missing or degenerate (e.g. $0 school median).
 * Do not surface those as real variance when the cohort school median is absent or non-positive with a ±100 sentinel.
 */
function peerVariancePercentDisplayValue(schoolMedianSalary: number | null, v: number | null): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  if (schoolMedianSalary == null || !Number.isFinite(schoolMedianSalary)) return null;
  if (schoolMedianSalary === 0 && Math.abs(Math.abs(v) - 100) < 0.05) return null;
  return v;
}

/** Order-insensitive comparison for multiselect id arrays. */
function sameIdSelection(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x.localeCompare(y));
  const sb = [...b].sort((x, y) => x.localeCompare(y));
  return sa.every((v, i) => v === sb[i]);
}

function severityStyles(level: SpreadSeverityLevel): { bar: string; text: string } {
  if (level === 'very_high') return { bar: 'bg-rose-500', text: 'text-rose-800' };
  if (level === 'high') return { bar: 'bg-amber-500', text: 'text-amber-900' };
  return { bar: 'bg-slate-400', text: 'text-slate-700' };
}

function spreadSeverityLevelFromApiLabel(label: string): SpreadSeverityLevel {
  const t = label.trim().toLowerCase();
  if (t.includes('very') && t.includes('high')) return 'very_high';
  if (t === 'high' || /\bhigh\b/.test(t)) return 'high';
  return 'moderate';
}

/** `spread` may be null when the cohort has no finite internal spread — row is still shown. */
type SpreadAlertRow = Omit<ExecutiveTopSpreadRow, 'spread'> & {
  spread: number | null;
  barColorHex?: string;
  source: TeacherEquitySchoolSpreadDto;
};
type VarianceAlertRow = ExecutiveBelowPeerRow & { source: TeacherEquitySchoolSpreadDto };

/** Trimmed query must already be lowercased; matches school name or educational center number. */
function schoolSpreadMatchesTableSearch(r: TeacherEquitySchoolSpreadDto, qLower: string): boolean {
  if (!qLower) return true;
  if (r.schoolName.toLowerCase().includes(qLower)) return true;
  const ec = (r.educationalCenterNumber ?? '').trim().toLowerCase();
  return ec.length > 0 && ec.includes(qLower);
}

function mapApiSchoolSpreadToTopRows(
  rows: TeacherEquitySchoolSpreadDto[],
  employmentType: EmploymentType
): SpreadAlertRow[] {
  return rows.map((r) => {
    const permanent = employmentType === 'permanent';
    const rawSpread = permanent ? r.permSPREAD : r.contSPREAD;
    const spread = rawSpread != null && Number.isFinite(rawSpread) ? rawSpread : null;
    const severityLabel = permanent ? r.permSeverityLabel : r.contSeverityLabel;
    const barColorHex = permanent ? r.permSeverityColor : r.contSeverityColor;
    if (spread != null) {
      const severity = spreadSeverityLevelFromApiLabel(severityLabel || 'Moderate');
      return {
        schoolId: r.id,
        schoolName: r.schoolName,
        districtLabel: r.district,
        spread,
        severity,
        severityLabel: severityLabel.trim() || spreadSeverityLabel(severity),
        barColorHex: barColorHex?.trim() || undefined,
        source: r,
      };
    }
    return {
      schoolId: r.id,
      schoolName: r.schoolName,
      districtLabel: r.district,
      spread: null,
      severity: 'moderate',
      severityLabel: '—',
      source: r,
    };
  });
}

function resolvedAvgPeerVarianceForCohort(
  r: TeacherEquitySchoolSpreadDto,
  employmentType: EmploymentType
): number | null {
  const v = employmentType === 'permanent' ? r.permAvgPeerVariance : r.contAvgPeerVariance;
  return v != null && Number.isFinite(v) ? v : null;
}

/** Worst comparison label for the selected cohort only (no Perm↔Cont cross-fill). */
function resolvedWorstComparisonForCohort(
  r: TeacherEquitySchoolSpreadDto,
  employmentType: EmploymentType
): string | null {
  const raw = employmentType === 'permanent' ? r.permWorstComparison : r.contWorstComparison;
  if (raw != null && String(raw).trim() !== '') return raw;
  return null;
}

function mapApiSchoolVarianceToBelowPeerRows(
  rows: TeacherEquitySchoolSpreadDto[],
  employmentType: EmploymentType
): VarianceAlertRow[] {
  return rows.map((r) => ({
    schoolId: r.id,
    schoolName: r.schoolName,
    districtLabel: r.district,
    avgPeerVariancePct: resolvedAvgPeerVarianceForCohort(r, employmentType),
    comparedAgainst: PEER_VARIANCE_DIMENSIONS_LABEL,
    suggestedPeerTab: 'by_district',
    source: r,
  }));
}

type SortDir = 'asc' | 'desc';
type SpreadAlertSortKey =
  | 'spread'
  | 'schoolName'
  | 'district'
  | 'headcount'
  | 'totalSalaryMedian'
  | 'districtPeerMedian';
type VarianceAlertSortKey =
  | 'avgPeerVariance'
  | 'schoolName'
  | 'district'
  | 'headcount'
  | 'districtVar'
  | 'enrollmentVar'
  | 'tuitionVar'
  | 'parentVar';

function cohortHeadcount(r: TeacherEquitySchoolSpreadDto, employmentType: EmploymentType): number {
  return employmentType === 'permanent' ? r.permCount : r.contCount;
}

/** Below-peer “School median salary”: `PermTotalSalaryMedian` (permanent) / `ContTotalSalaryMedian` (contractual). */
function cohortTotalSalaryMedian(
  r: TeacherEquitySchoolSpreadDto,
  employmentType: EmploymentType
): number | null {
  const v = employmentType === 'permanent' ? r.permTotalSalaryMedian : r.contTotalSalaryMedian;
  return v != null && Number.isFinite(v) ? v : null;
}

function cohortDistrictMedian(r: TeacherEquitySchoolSpreadDto, employmentType: EmploymentType): number | null {
  const v =
    employmentType === 'permanent' ? r.perDistrictMedianTotalSalary : r.contDistrictMedianTotalSalary;
  return v != null && Number.isFinite(v) ? v : null;
}

/** Below-peer “Dist. var %”: `PermDistrictVariance` / `ContDistrictVariance`. */
function cohortDistrictVar(r: TeacherEquitySchoolSpreadDto, employmentType: EmploymentType): number | null {
  const v = employmentType === 'permanent' ? r.permDistrictVariance : r.contDistrictVariance;
  return v != null && Number.isFinite(v) ? v : null;
}

/** Below-peer “Enroll. var %”: `PermSchoolBandVariance` / `ContSchoolBandVariance`. */
function cohortSchoolBandVar(r: TeacherEquitySchoolSpreadDto, employmentType: EmploymentType): number | null {
  const v = employmentType === 'permanent' ? r.permSchoolBandVariance : r.contSchoolBandVariance;
  return v != null && Number.isFinite(v) ? v : null;
}

/** Below-peer “Tuition var %”: `PermTuitionBandVariance` / `ContTuitionBandVariance`. */
function cohortTuitionBandVar(r: TeacherEquitySchoolSpreadDto, employmentType: EmploymentType): number | null {
  const v = employmentType === 'permanent' ? r.permTuitionBandVariance : r.contTuitionBandVariance;
  return v != null && Number.isFinite(v) ? v : null;
}

/** Below-peer “Parent var %”: `PermParentTuitionBandVariance` / `ContParentTuitionBandVariance`. */
function cohortParentTuitionBandVar(r: TeacherEquitySchoolSpreadDto, employmentType: EmploymentType): number | null {
  const v = employmentType === 'permanent' ? r.permParentTuitionBandVariance : r.contParentTuitionBandVariance;
  return v != null && Number.isFinite(v) ? v : null;
}

function cohortSchoolBandMedianTotalSalary(
  r: TeacherEquitySchoolSpreadDto,
  employmentType: EmploymentType
): number | null {
  const v =
    employmentType === 'permanent' ? r.perSchoolBandMedianTotalSalary : r.contSchoolBandMedianTotalSalary;
  return v != null && Number.isFinite(v) ? v : null;
}

function cohortTuitionBandMedianTotalSalary(
  r: TeacherEquitySchoolSpreadDto,
  employmentType: EmploymentType
): number | null {
  const v =
    employmentType === 'permanent' ? r.perTuitionBandMedianTotalSalary : r.contTuitionBandMedianTotalSalary;
  return v != null && Number.isFinite(v) ? v : null;
}

function cohortParentTuitionBandMedianTotalSalary(
  r: TeacherEquitySchoolSpreadDto,
  employmentType: EmploymentType
): number | null {
  const v =
    employmentType === 'permanent'
      ? r.perParentTuitionBandMedianTotalSalary
      : r.contParentTuitionBandMedianTotalSalary;
  return v != null && Number.isFinite(v) ? v : null;
}

const SPREAD_ALERT_EXPORT_HEADERS: string[] = [
  'School name',
  'Educational center #',
  'District',
  'Teachers',
  'School average salary',
  'District peer median',
  'School size band median',
  'Tuition band median',
  'Parent tuition median',
  'Spread',
  'Severity',
];

const BELOW_PEER_EXPORT_HEADERS: string[] = [
  'School name',
  'Educational center #',
  'District',
  'Teachers',
  'Avg. peer variance',
  'School average salary',
  'Dist. var %',
  'Size band var %',
  'Tuition var %',
  'Parent var %',
  'Compared against',
  'Largest reported gap',
];

function buildSpreadAlertExportMatrix(
  rows: SpreadAlertRow[],
  employmentType: EmploymentType
): string[][] {
  return rows.map((row) => {
    const src = row.source;
    const medSal = cohortTotalSalaryMedian(src, employmentType);
    const distMed = cohortDistrictMedian(src, employmentType);
    const enrollMed = cohortSchoolBandMedianTotalSalary(src, employmentType);
    const tuitionMed = cohortTuitionBandMedianTotalSalary(src, employmentType);
    const parentMed = cohortParentTuitionBandMedianTotalSalary(src, employmentType);
    return [
      row.schoolName,
      (src.educationalCenterNumber ?? '').trim() || '—',
      row.districtLabel,
      String(cohortHeadcount(src, employmentType)),
      medSal != null ? fmtMoney(medSal) : '—',
      distMed != null ? fmtMoney(distMed) : '—',
      enrollMed != null ? fmtMoney(enrollMed) : '—',
      tuitionMed != null ? fmtMoney(tuitionMed) : '—',
      parentMed != null ? fmtMoney(parentMed) : '—',
      fmtMoneyOrDash(row.spread),
      row.severityLabel,
    ];
  });
}

function buildBelowPeerExportMatrix(
  rows: VarianceAlertRow[],
  employmentType: EmploymentType
): string[][] {
  return rows.map((row) => {
    const src = row.source;
    const medSal = cohortTotalSalaryMedian(src, employmentType);
    const dv = cohortDistrictVar(src, employmentType);
    const ev = cohortSchoolBandVar(src, employmentType);
    const tv = cohortTuitionBandVar(src, employmentType);
    const pv = cohortParentTuitionBandVar(src, employmentType);
    const worst = resolvedWorstComparisonForCohort(src, employmentType);
    const avgDisp = peerVariancePercentDisplayValue(medSal, row.avgPeerVariancePct);
    return [
      row.schoolName,
      (src.educationalCenterNumber ?? '').trim() || '—',
      row.districtLabel,
      String(cohortHeadcount(src, employmentType)),
      fmtPctSigned(avgDisp),
      medSal != null ? fmtMoney(medSal) : '—',
      fmtPctSigned(peerVariancePercentDisplayValue(medSal, dv)),
      fmtPctSigned(peerVariancePercentDisplayValue(medSal, ev)),
      fmtPctSigned(peerVariancePercentDisplayValue(medSal, tv)),
      fmtPctSigned(peerVariancePercentDisplayValue(medSal, pv)),
      PEER_VARIANCE_DIMENSIONS_LABEL,
      worst ?? '—',
    ];
  });
}

function compareSpreadAlertRows(
  a: SpreadAlertRow,
  b: SpreadAlertRow,
  key: SpreadAlertSortKey,
  dir: SortDir,
  employmentType: EmploymentType
): number {
  const m = dir === 'asc' ? 1 : -1;
  if (key === 'spread') {
    const aHas = a.spread != null && Number.isFinite(a.spread);
    const bHas = b.spread != null && Number.isFinite(b.spread);
    if (aHas !== bHas) return aHas ? -1 : 1;
    if (!aHas) return 0;
    return (a.spread! - b.spread!) * m;
  }
  if (key === 'schoolName') return a.schoolName.localeCompare(b.schoolName, undefined, { sensitivity: 'base' }) * m;
  if (key === 'district') return a.districtLabel.localeCompare(b.districtLabel, undefined, { sensitivity: 'base' }) * m;
  if (key === 'headcount')
    return (cohortHeadcount(a.source, employmentType) - cohortHeadcount(b.source, employmentType)) * m;
  if (key === 'totalSalaryMedian') {
    const na = cohortTotalSalaryMedian(a.source, employmentType);
    const nb = cohortTotalSalaryMedian(b.source, employmentType);
    return ((na ?? -Infinity) - (nb ?? -Infinity)) * m;
  }
  const da = cohortDistrictMedian(a.source, employmentType);
  const db = cohortDistrictMedian(b.source, employmentType);
  return ((da ?? -Infinity) - (db ?? -Infinity)) * m;
}

function compareVarianceAlertRows(
  a: VarianceAlertRow,
  b: VarianceAlertRow,
  key: VarianceAlertSortKey,
  dir: SortDir,
  employmentType: EmploymentType
): number {
  const m = dir === 'asc' ? 1 : -1;
  if (key === 'avgPeerVariance') {
    const aHas = a.avgPeerVariancePct != null && Number.isFinite(a.avgPeerVariancePct);
    const bHas = b.avgPeerVariancePct != null && Number.isFinite(b.avgPeerVariancePct);
    if (aHas !== bHas) return aHas ? -1 : 1;
    if (!aHas) return 0;
    return (a.avgPeerVariancePct! - b.avgPeerVariancePct!) * m;
  }
  if (key === 'schoolName') return a.schoolName.localeCompare(b.schoolName, undefined, { sensitivity: 'base' }) * m;
  if (key === 'district') return a.districtLabel.localeCompare(b.districtLabel, undefined, { sensitivity: 'base' }) * m;
  if (key === 'headcount')
    return (cohortHeadcount(a.source, employmentType) - cohortHeadcount(b.source, employmentType)) * m;
  if (key === 'districtVar')
    return (
      ((cohortDistrictVar(a.source, employmentType) ?? -Infinity) -
        (cohortDistrictVar(b.source, employmentType) ?? -Infinity)) *
      m
    );
  if (key === 'enrollmentVar')
    return (
      ((cohortSchoolBandVar(a.source, employmentType) ?? -Infinity) -
        (cohortSchoolBandVar(b.source, employmentType) ?? -Infinity)) *
      m
    );
  if (key === 'tuitionVar')
    return (
      ((cohortTuitionBandVar(a.source, employmentType) ?? -Infinity) -
        (cohortTuitionBandVar(b.source, employmentType) ?? -Infinity)) *
      m
    );
  return (
    ((cohortParentTuitionBandVar(a.source, employmentType) ?? -Infinity) -
      (cohortParentTuitionBandVar(b.source, employmentType) ?? -Infinity)) *
    m
  );
}

function labelEmployment(e: EmploymentType): string {
  return e === 'permanent' ? 'Permanent' : 'Contractual';
}

function EmploymentTypeSwitch({
  value,
  onChange,
}: {
  value: EmploymentType;
  onChange: (next: EmploymentType) => void;
}) {
  const contractual = value === 'contractual';
  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <span
        className={`text-[11px] font-semibold uppercase tracking-wide ${
          !contractual ? 'text-slate-700' : 'text-slate-400'
        }`}
      >
        Permanent
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={contractual}
        aria-label={`Employment type: ${labelEmployment(value)}. Click to switch.`}
        onClick={() => onChange(contractual ? 'permanent' : 'contractual')}
        className="relative h-8 w-14 shrink-0 rounded-full border border-slate-300/45 bg-gradient-to-b from-slate-200/55 to-slate-200/35 shadow-inner transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 focus-visible:ring-offset-2"
      >
        <span
          className={`absolute left-0.5 top-0.5 h-7 w-7 rounded-full border border-white/70 bg-white/95 shadow-sm transition-transform duration-200 ease-out ${
            contractual ? 'translate-x-6' : 'translate-x-0'
          }`}
        />
      </button>
      <span
        className={`text-[11px] font-semibold uppercase tracking-wide ${
          contractual ? 'text-stone-700' : 'text-slate-400'
        }`}
      >
        Contractual
      </span>
    </div>
  );
}

function TeacherEquityLoadingState({ yearLabel }: { yearLabel: string }) {
  return (
    <section
      className="mt-10 rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50/90 px-6 py-16 shadow-md ring-1 ring-indigo-100/80 sm:px-10 sm:py-20"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/80">
          <Loader2 className="size-8 animate-spin" strokeWidth={2} aria-hidden />
        </div>
        <h2 className="mt-6 text-lg font-bold tracking-tight text-slate-900">Loading report</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          Loading national figures and school lists for{' '}
          <strong className="text-slate-900">{yearLabel || 'the selected year'}</strong>. This usually takes a few seconds.
        </p>
        <span className="sr-only">Loading…</span>
      </div>
    </section>
  );
}

function TeacherEquityYearLanding() {
  return (
    <section className="mt-10 rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50/90 px-6 py-12 shadow-md ring-1 ring-indigo-100/80 sm:px-10 sm:py-14">
      <div className="mx-auto max-w-2xl text-center">
        <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/80">
          <BookOpen className="size-7" strokeWidth={1.5} aria-hidden />
        </div>
        <h2 className="mt-6 text-xl font-bold tracking-tight text-slate-900">Select an academic year to continue</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Use the <strong>Reporting year</strong> control at the top of the page. Until a year is chosen, national KPIs and
          alert tables stay hidden so you always know which reporting period you are viewing.
        </p>
      </div>
      <div className="mx-auto mt-10 max-w-3xl rounded-xl border border-slate-200/80 bg-white/90 px-5 py-6 text-left shadow-sm ring-1 ring-slate-100 sm:px-8">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-800">What this report provides</h3>
        <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
          <li>
            <strong className="text-slate-800">National overview</strong> — overall median salary and average internal salary
            spread for permanent and contractual teachers for the reporting year you select.
          </li>
          <li>
            <strong className="text-slate-800">Filters</strong> — optional role and qualification selections (leave empty for
            all). Press <strong>Search</strong> to refresh the figures with your choices.
          </li>
          <li>
            <strong className="text-slate-800">School lists</strong> — schools with the widest internal salary gaps and those
            furthest below peer medians. Select a row to view that school&apos;s teacher budget detail.
          </li>
        </ul>
      </div>
    </section>
  );
}

const BUDGET_TEACHER_TABLE_COL_SPAN = 23;

/** School drill-down: budget teacher lines for the selected school and year. */
function SchoolTeachersDrawer({
  open,
  onClose,
  schoolName,
  districtLabel,
  year,
  employmentType,
  budgetId,
  appliedRoleIds,
  appliedQualificationIds,
}: {
  open: boolean;
  onClose: () => void;
  schoolName: string;
  districtLabel: string;
  year: number;
  employmentType: EmploymentType;
  budgetId: string | null;
  appliedRoleIds: string[];
  appliedQualificationIds: string[];
}) {
  const budgetQuery = useBudgetTeacherPartialInfoQuery(budgetId, employmentType, open);
  const rawBudgetRows = teacherRowsFromBudgetPartial(budgetQuery.data ?? null);
  const visibleBudgetRows = useMemo(
    () => filterBudgetTeachersByAppliedPickLists(rawBudgetRows, appliedRoleIds, appliedQualificationIds),
    [rawBudgetRows, appliedRoleIds, appliedQualificationIds]
  );

  const drawerTotalSalaryUsd = useMemo(
    () => sumBudgetTeacherSalaryTotalUsd(visibleBudgetRows, budgetQuery.data?.exchangeRateAmount),
    [visibleBudgetRows, budgetQuery.data?.exchangeRateAmount]
  );

  const cohortFilterActive = appliedRoleIds.length > 0 || appliedQualificationIds.length > 0;

  const [teacherTablePage, setTeacherTablePage] = useState(1);
  const [teacherTablePageSize, setTeacherTablePageSize] = useState(BUDGET_TEACHER_DRAWER_PAGE_DEFAULT);
  const [teacherTableSearch, setTeacherTableSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setTeacherTablePage(1);
    setTeacherTableSearch('');
  }, [open, budgetId, employmentType, appliedRoleIds, appliedQualificationIds]);

  useEffect(() => {
    setTeacherTablePage(1);
  }, [teacherTableSearch]);

  const tableRowsAfterSearch = useMemo(() => {
    const q = teacherTableSearch.trim().toLowerCase();
    if (!q) return visibleBudgetRows;
    return visibleBudgetRows.filter((r) => {
      const name = (r.name ?? '').toLowerCase();
      const fund = (r.fundId ?? '').trim().toLowerCase();
      return name.includes(q) || fund.includes(q);
    });
  }, [visibleBudgetRows, teacherTableSearch]);

  const cohortRowCount = visibleBudgetRows.length;
  const tableRowCount = tableRowsAfterSearch.length;

  const drawerMedianSalaryUsd = useMemo(() => {
    if (cohortRowCount === 0 || drawerTotalSalaryUsd == null) return null;
    return drawerTotalSalaryUsd / cohortRowCount;
  }, [cohortRowCount, drawerTotalSalaryUsd]);

  const teacherPageCount = Math.max(1, Math.ceil(tableRowCount / teacherTablePageSize));
  const teacherPageSafe = Math.min(Math.max(1, teacherTablePage), teacherPageCount);

  const teacherPagedRows = useMemo(() => {
    const start = (teacherPageSafe - 1) * teacherTablePageSize;
    return tableRowsAfterSearch.slice(start, start + teacherTablePageSize);
  }, [tableRowsAfterSearch, teacherPageSafe, teacherTablePageSize]);

  const showFrom = tableRowCount === 0 ? 0 : (teacherPageSafe - 1) * teacherTablePageSize + 1;
  const showTo = tableRowCount === 0 ? 0 : Math.min(teacherPageSafe * teacherTablePageSize, tableRowCount);

  const budgetBody =
    !budgetId?.trim() ? (
      <Alert type="info" showIcon message="No budget linked for this school." />
    ) : budgetQuery.isFetching && !budgetQuery.data ? (
      <div className="flex items-center gap-2 py-10 text-sm text-slate-600">
        <Loader2 className="size-5 animate-spin text-indigo-600" aria-hidden />
        Loading budget teachers…
      </div>
    ) : budgetQuery.isError ? (
      <Alert
        type="error"
        showIcon
        message="Could not load budget teachers"
        description={
          budgetQuery.error instanceof ApiError
            ? ApiError.getUserMessage(budgetQuery.error)
            : budgetQuery.error instanceof Error
              ? budgetQuery.error.message
              : 'The teacher list could not be loaded. Please try again.'
        }
      />
    ) : (
      <div className="space-y-4">
        <div className="flex flex-col gap-1 border-b border-slate-200 pb-3">
          <p className="text-sm font-medium text-slate-800">
            {labelEmployment(employmentType)} teachers · reporting year {year}
          </p>
          {cohortFilterActive ? (
            <Alert
              type="info"
              showIcon
              className="mt-2 text-xs"
              message="Role and qualification filters active"
              description="Only teachers whose role and qualification match your current filters are counted and listed."
            />
          ) : null}
          {budgetQuery.data?.exchangeRateAmount != null ? (
            <p className="mt-1 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Budget exchange (LL per USD):</span>{' '}
              {fmtBudgetLumpSum(budgetQuery.data.exchangeRateAmount)}
            </p>
          ) : (
            <p className="mt-1 text-xs text-amber-800">
              Budget response has no exchange rate; total salary in USD cannot be computed.
            </p>
          )}
        </div>

        <section aria-label="School teacher statistics">
          <h3 className="sr-only">School teacher statistics</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 px-4 py-3.5 shadow-sm ring-1 ring-slate-100/80">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Teacher count</p>
                  <p className="mt-1.5 text-2xl font-bold tabular-nums tracking-tight text-slate-900">
                    {cohortRowCount}
                  </p>
                  {cohortFilterActive && rawBudgetRows.length !== cohortRowCount ? (
                    <p className="mt-1 text-[11px] leading-snug text-slate-500">
                      {rawBudgetRows.length} in budget before filters
                    </p>
                  ) : null}
                </div>
                <div className="rounded-lg bg-indigo-50 p-2 ring-1 ring-indigo-100/80">
                  <Users className="size-5 text-indigo-600" aria-hidden />
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 px-4 py-3.5 shadow-sm ring-1 ring-slate-100/80">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total salary (USD)</p>
                  <p className="mt-1.5 truncate text-2xl font-bold tabular-nums tracking-tight text-slate-900">
                    {drawerTotalSalaryUsd != null
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(drawerTotalSalaryUsd)
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-2 ring-1 ring-emerald-100/80">
                  <CircleDollarSign className="size-5 text-emerald-700" aria-hidden />
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 px-4 py-3.5 shadow-sm ring-1 ring-slate-100/80 sm:col-span-2 lg:col-span-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Median total salary (USD)
                  </p>
                  <p className="mt-1.5 truncate text-2xl font-bold tabular-nums tracking-tight text-slate-900">
                    {drawerMedianSalaryUsd != null
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(drawerMedianSalaryUsd)
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-violet-50 p-2 ring-1 ring-violet-100/80">
                  <Scale className="size-5 text-violet-700" aria-hidden />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-md">
          <Input
            id="drawer-teacher-table-search"
            type="search"
            autoComplete="off"
            leftIcon={<Search className="size-4 shrink-0" aria-hidden />}
            placeholder="Search by teacher name or fund ID"
            value={teacherTableSearch}
            onChange={(e) => setTeacherTableSearch(e.target.value)}
            aria-label="Search teachers by name or fund ID"
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1600px] border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                  <th className="whitespace-nowrap px-2 py-2">Fund ID</th>
                  <th className="min-w-[8rem] px-2 py-2">Name</th>
                  <th className="px-2 py-2">Role</th>
                  <th className="min-w-[6rem] px-2 py-2">Ed. level</th>
                  <th className="px-2 py-2">Qual.</th>
                  <th className="whitespace-nowrap px-2 py-2">Start</th>
                  <th className="min-w-[6.5rem] whitespace-nowrap bg-slate-200/90 px-2 py-2 text-right text-slate-700">
                    Total Salary L.L
                  </th>
                  <th className="min-w-[6.5rem] whitespace-nowrap bg-slate-200/90 px-2 py-2 text-right text-slate-700">
                    Total Salary U.S.D
                  </th>
                  <th className="whitespace-nowrap px-2 py-2">Work start</th>
                  <th className="px-2 py-2 text-right">H/wk perm</th>
                  <th className="px-2 py-2 text-right">H/wk cont.</th>
                  <th className="px-2 py-2 text-right">Basic LL</th>
                  <th className="px-2 py-2 text-right">Soc. LL</th>
                  <th className="px-2 py-2 text-right">Soc. USD</th>
                  <th className="px-2 py-2 text-right">Retro LL</th>
                  <th className="px-2 py-2 text-right">Above quota</th>
                  <th className="px-2 py-2 text-right">&gt;35h</th>
                  <th className="px-2 py-2 text-right">Bonus LL</th>
                  <th className="px-2 py-2 text-right">Add. tasks</th>
                  <th className="px-2 py-2 text-right">Family LL</th>
                  <th className="px-2 py-2 text-right">Transport LL</th>
                  <th className="px-2 py-2 text-right">NSSF LL</th>
                  <th className="px-2 py-2 text-right">Comp. fund LL</th>
                </tr>
              </thead>
              <tbody>
                {rawBudgetRows.length === 0 ? (
                  <tr>
                    <td colSpan={BUDGET_TEACHER_TABLE_COL_SPAN} className="px-3 py-8 text-center text-sm text-slate-500">
                      No teacher lines were returned for this school with the current filters.
                    </td>
                  </tr>
                ) : null}
                {rawBudgetRows.length > 0 && visibleBudgetRows.length === 0 ? (
                  <tr>
                    <td colSpan={BUDGET_TEACHER_TABLE_COL_SPAN} className="px-3 py-8 text-center text-sm text-slate-600">
                      No teachers in this budget match the current role or qualification filters. Clear those filters, press{' '}
                      <strong>Search</strong> on the main report, then open this school again.
                    </td>
                  </tr>
                ) : null}
                {cohortRowCount > 0 && tableRowCount === 0 ? (
                  <tr>
                    <td colSpan={BUDGET_TEACHER_TABLE_COL_SPAN} className="px-3 py-8 text-center text-sm text-slate-600">
                      No teachers match &quot;{teacherTableSearch.trim()}&quot;. Try another name or fund ID.
                    </td>
                  </tr>
                ) : null}
                {teacherPagedRows.map((r) => {
                  const items = r.budgetTeacherPickListItems;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[11px] text-slate-800">
                        {r.fundId?.trim() ? r.fundId : '—'}
                      </td>
                      <td className="max-w-[12rem] px-2 py-1.5 font-medium text-slate-900">{r.name}</td>
                      <td className="px-2 py-1.5 text-slate-700">{budgetPickFirstTitle(items, 'TeacherRole')}</td>
                      <td className="max-w-[10rem] px-2 py-1.5 text-slate-700">{budgetPickEducationTitles(items)}</td>
                      <td className="px-2 py-1.5 text-slate-700">
                        {budgetPickFirstTitle(items, 'TeacherQualification')}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{fmtBudgetDdMmYyyy(r.startDate)}</td>
                      <td className="bg-slate-50/80 px-2 py-1.5 text-right font-mono tabular-nums font-semibold text-slate-900">
                        {fmtBudgetTotalSalaryLl(r.totalSalaryLL)}
                      </td>
                      <td className="bg-slate-50/80 px-2 py-1.5 text-right font-mono tabular-nums font-semibold text-slate-900">
                        {fmtBudgetTotalSalaryUsd(r.totalSalaryUSD)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{fmtBudgetDdMmYyyy(r.workStartDate)}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {r.weeklyHoursPerm != null && Number.isFinite(r.weeklyHoursPerm)
                          ? String(r.weeklyHoursPerm)
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {r.weeklyHoursContract != null && Number.isFinite(r.weeklyHoursContract)
                          ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(r.weeklyHoursContract)
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.basicSalary)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.socialAssistanceLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.socialAssistanceUSD)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.retroEffectLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.compensationWorkDoneAboveQuotaLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.compensationWorkGreater35HoursLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.bonuseAssistanceLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.additionalTasksFees)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.familyContributionLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.transportationLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.nssfContributionLL)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtBudgetLumpSum(r.compensationFundLL)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tableRowCount > 0 ? (
            <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50/90 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11px] font-medium tabular-nums text-slate-600">
                Showing{' '}
                <span className="font-semibold text-slate-900">
                  {showFrom}-{showTo}
                </span>{' '}
                of <span className="font-semibold text-slate-900">{tableRowCount}</span>
                {teacherTableSearch.trim() ? (
                  <span className="text-slate-500"> (of {cohortRowCount} after filters)</span>
                ) : null}
              </p>
              <Pagination
                size="small"
                current={teacherPageSafe}
                pageSize={teacherTablePageSize}
                total={tableRowCount}
                showSizeChanger
                pageSizeOptions={[...BUDGET_TEACHER_DRAWER_PAGE_SIZES]}
                onChange={(p, ps) => {
                  const nextSize = ps ?? teacherTablePageSize;
                  if (nextSize !== teacherTablePageSize) {
                    setTeacherTablePageSize(nextSize);
                    setTeacherTablePage(1);
                  } else {
                    setTeacherTablePage(p);
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    );

  return (
    <Drawer
      title={<span className="font-semibold text-slate-900">School — budget teachers</span>}
      placement="right"
      width={Math.min(1120, typeof window !== 'undefined' ? window.innerWidth - 24 : 1120)}
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3">
          <div className="flex items-start gap-2">
            <School className="mt-0.5 size-5 shrink-0 text-indigo-600" aria-hidden />
            <div className="min-w-0">
              <p className="text-base font-bold text-slate-900">{schoolName}</p>
              <p className="mt-1 text-sm text-slate-600">
                <span className="font-medium text-slate-800">District:</span> {districtLabel || '—'}
              </p>
            </div>
          </div>
        </div>

        {budgetBody}
      </div>
    </Drawer>
  );
}

export function TeacherEquityReport() {
  const reportYearsQuery = useBudgetReportYearsListQuery();
  const defaultYear = useDefaultYear();
  const spreadSeverityVeryHighRatioResolved = useTeacherEquitySpreadSeverityVeryHighRatioResolved();
  const spreadSeverityHighRatioResolved = useTeacherEquitySpreadSeverityHighRatioResolved();
  const spreadSeverityVeryHighAmountResolved = useTeacherEquitySpreadSeverityVeryHighAmountResolved();
  const spreadSeverityHighAmountResolved = useTeacherEquitySpreadSeverityHighAmountResolved();
  const teacherEquitySpreadSettings = [
    spreadSeverityVeryHighRatioResolved,
    spreadSeverityHighRatioResolved,
    spreadSeverityVeryHighAmountResolved,
    spreadSeverityHighAmountResolved,
  ];

  const [academicYearId, setAcademicYearId] = useState('');

  useEffect(() => {
    if (!academicYearId && defaultYear) setAcademicYearId(defaultYear);
  }, [defaultYear]);
  const [employmentType, setEmploymentType] = useState<EmploymentType>('permanent');
  /** Role and qualification picks in the filter bar; applied when the user presses Search. */
  const [draftRoleIds, setDraftRoleIds] = useState<string[]>([]);
  const [draftQualificationIds, setDraftQualificationIds] = useState<string[]>([]);
  /** Last applied role/qualification filters — single update so React Query sees one key change per Search. */
  const [appliedCohortFilters, setAppliedCohortFilters] = useState<{
    roleIds: string[];
    qualificationIds: string[];
  }>({ roleIds: [], qualificationIds: [] });

  const [spreadAlertPage, setSpreadAlertPage] = useState(0);
  const [spreadAlertSearch, setSpreadAlertSearch] = useState('');
  const [spreadAlertDistrict, setSpreadAlertDistrict] = useState('');
  const [spreadAlertSortKey, setSpreadAlertSortKey] = useState<SpreadAlertSortKey>('spread');
  const [spreadAlertSortDir, setSpreadAlertSortDir] = useState<SortDir>('desc');

  const [varianceAlertPage, setVarianceAlertPage] = useState(0);
  const [varianceAlertSearch, setVarianceAlertSearch] = useState('');
  const [varianceAlertDistrict, setVarianceAlertDistrict] = useState('');
  const [varianceAlertSortKey, setVarianceAlertSortKey] = useState<VarianceAlertSortKey>('avgPeerVariance');
  const [varianceAlertSortDir, setVarianceAlertSortDir] = useState<SortDir>('asc');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSchool, setDrawerSchool] = useState<{
    id: string;
    name: string;
    district: string;
    budgetId: string | null;
  } | null>(null);

  const rolesQuery = usePickListQuery(PICK_LIST_CODES.teacherRole);
  const qualificationsQuery = usePickListQuery(PICK_LIST_CODES.teacherQualification);

  const yearSelected = Boolean(academicYearId.trim());

  const budgetYearOptions = useMemo(() => {
    const raw = reportYearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ id: String(n), label: String(n) }));
  }, [reportYearsQuery.data]);

  const academicYearLabel = yearSelected
    ? (budgetYearOptions.find((y) => y.id === academicYearId)?.label ?? academicYearId)
    : '';

  const teacherEquityApiYear = useMemo(() => {
    const d = reportingYearDigitsFromAcademicYearId(academicYearId);
    if (!d) return null;
    const y = Number(d);
    return Number.isFinite(y) && y > 0 ? y : null;
  }, [academicYearId]);

  const nationalFilters = useMemo(
    () => ({
      year: yearSelected ? teacherEquityApiYear : null,
      roleIds: appliedCohortFilters.roleIds,
      qualificationIds: appliedCohortFilters.qualificationIds,
    }),
    [yearSelected, teacherEquityApiYear, appliedCohortFilters]
  );

  const nationalKpisQuery = useTeacherEquityNationalKpisQuery(nationalFilters);
  const { refetch: refetchNationalKpis, isFetching: nationalKpisFetching } = nationalKpisQuery;

  const filtersPendingApply = useMemo(
    () =>
      !sameIdSelection(draftRoleIds, appliedCohortFilters.roleIds) ||
      !sameIdSelection(draftQualificationIds, appliedCohortFilters.qualificationIds),
    [draftRoleIds, draftQualificationIds, appliedCohortFilters]
  );

  const applyNationalFilters = useCallback(() => {
    const nextRoles = [...draftRoleIds];
    const nextQuals = [...draftQualificationIds];
    const unchanged =
      sameIdSelection(nextRoles, appliedCohortFilters.roleIds) &&
      sameIdSelection(nextQuals, appliedCohortFilters.qualificationIds);
    if (unchanged) {
      if (yearSelected && teacherEquityApiYear != null) void refetchNationalKpis();
      return;
    }
    setAppliedCohortFilters({ roleIds: nextRoles, qualificationIds: nextQuals });
  }, [
    draftRoleIds,
    draftQualificationIds,
    appliedCohortFilters,
    yearSelected,
    teacherEquityApiYear,
    refetchNationalKpis,
  ]);

  const employmentLabel = labelEmployment(employmentType);

  const schoolSpreadDistrictOptions = useMemo(() => {
    const list = nationalKpisQuery.data?.schoolSpreads ?? [];
    const s = new Set<string>();
    for (const r of list) {
      if (r.district && r.district !== '—') s.add(r.district);
    }
    return [...s].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [nationalKpisQuery.data?.schoolSpreads]);

  const topSpreadAlertsSorted = useMemo((): SpreadAlertRow[] => {
    const list = nationalKpisQuery.data?.schoolSpreads ?? [];
    const q = spreadAlertSearch.trim().toLowerCase();
    const filtered = list.filter((r) => {
      if (cohortHeadcount(r, employmentType) === 0) return false;
      if (!schoolSpreadMatchesTableSearch(r, q)) return false;
      if (spreadAlertDistrict && r.district !== spreadAlertDistrict) return false;
      return true;
    });
    const mapped = mapApiSchoolSpreadToTopRows(filtered, employmentType);
    return [...mapped].sort((a, b) =>
      compareSpreadAlertRows(a, b, spreadAlertSortKey, spreadAlertSortDir, employmentType)
    );
  }, [
    employmentType,
    nationalKpisQuery.data?.schoolSpreads,
    spreadAlertSearch,
    spreadAlertDistrict,
    spreadAlertSortKey,
    spreadAlertSortDir,
  ]);

  const topSpreadAlerts = useMemo(() => {
    const start = spreadAlertPage * SCHOOL_ALERTS_PAGE_SIZE;
    return topSpreadAlertsSorted.slice(start, start + SCHOOL_ALERTS_PAGE_SIZE);
  }, [topSpreadAlertsSorted, spreadAlertPage]);

  const belowPeerAlertsSorted = useMemo((): VarianceAlertRow[] => {
    const list = nationalKpisQuery.data?.schoolSpreads ?? [];
    const q = varianceAlertSearch.trim().toLowerCase();
    const filtered = list.filter((r) => {
      if (cohortHeadcount(r, employmentType) === 0) return false;
      if (!schoolSpreadMatchesTableSearch(r, q)) return false;
      if (varianceAlertDistrict && r.district !== varianceAlertDistrict) return false;
      return true;
    });
    const mapped = mapApiSchoolVarianceToBelowPeerRows(filtered, employmentType);
    return [...mapped].sort((a, b) =>
      compareVarianceAlertRows(a, b, varianceAlertSortKey, varianceAlertSortDir, employmentType)
    );
  }, [
    employmentType,
    nationalKpisQuery.data?.schoolSpreads,
    varianceAlertSearch,
    varianceAlertDistrict,
    varianceAlertSortKey,
    varianceAlertSortDir,
  ]);

  const belowPeerAlerts = useMemo(() => {
    const start = varianceAlertPage * SCHOOL_ALERTS_PAGE_SIZE;
    return belowPeerAlertsSorted.slice(start, start + SCHOOL_ALERTS_PAGE_SIZE);
  }, [belowPeerAlertsSorted, varianceAlertPage]);

  useEffect(() => {
    setSpreadAlertPage(0);
  }, [
    employmentType,
    spreadAlertSearch,
    spreadAlertDistrict,
    nationalKpisQuery.data?.schoolSpreads,
    appliedCohortFilters,
  ]);

  useEffect(() => {
    setVarianceAlertPage(0);
  }, [
    employmentType,
    varianceAlertSearch,
    varianceAlertDistrict,
    nationalKpisQuery.data?.schoolSpreads,
    appliedCohortFilters,
  ]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(topSpreadAlertsSorted.length / SCHOOL_ALERTS_PAGE_SIZE) - 1);
    setSpreadAlertPage((p) => Math.min(p, maxPage));
  }, [topSpreadAlertsSorted.length]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(belowPeerAlertsSorted.length / SCHOOL_ALERTS_PAGE_SIZE) - 1);
    setVarianceAlertPage((p) => Math.min(p, maxPage));
  }, [belowPeerAlertsSorted.length]);

  const toggleSpreadAlertSort = useCallback(
    (key: SpreadAlertSortKey) => {
      if (spreadAlertSortKey === key) setSpreadAlertSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setSpreadAlertSortKey(key);
        setSpreadAlertSortDir(key === 'spread' || key === 'totalSalaryMedian' || key === 'districtPeerMedian' ? 'desc' : 'asc');
      }
    },
    [spreadAlertSortKey]
  );

  const toggleVarianceAlertSort = useCallback(
    (key: VarianceAlertSortKey) => {
      if (varianceAlertSortKey === key) setVarianceAlertSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setVarianceAlertSortKey(key);
        setVarianceAlertSortDir(key === 'avgPeerVariance' || key === 'headcount' ? 'asc' : 'desc');
      }
    },
    [varianceAlertSortKey]
  );

  const spreadAlertPageCount = useMemo(
    () => Math.max(1, Math.ceil(topSpreadAlertsSorted.length / SCHOOL_ALERTS_PAGE_SIZE)),
    [topSpreadAlertsSorted.length]
  );

  const varianceAlertPageCount = useMemo(
    () => Math.max(1, Math.ceil(belowPeerAlertsSorted.length / SCHOOL_ALERTS_PAGE_SIZE)),
    [belowPeerAlertsSorted.length]
  );

  const openSchoolDrawer = useCallback(
    (id: string, name: string, district: string, budgetId: string | null) => {
      setDrawerSchool({ id, name, district, budgetId });
      setDrawerOpen(true);
    },
    []
  );

  const handleExportSpreadExcel = useCallback(() => {
    if (topSpreadAlertsSorted.length === 0) {
      void message.warning('No rows to export for this table.');
      return;
    }
    const matrix = buildSpreadAlertExportMatrix(topSpreadAlertsSorted, employmentType);
    const base = `teacher-equity_spread-alerts_${teacherEquityApiYear ?? 'year'}_${employmentType}`;
    void (async () => {
      try {
        await downloadStringMatrixAsXlsx(base, 'Spread alerts', SPREAD_ALERT_EXPORT_HEADERS, matrix);
        void message.success(`Downloaded ${matrix.length} rows to Excel.`);
      } catch (e) {
        void message.error(e instanceof Error ? e.message : 'Excel export failed.');
      }
    })();
  }, [topSpreadAlertsSorted, employmentType, teacherEquityApiYear]);

  const handleExportSpreadPdf = useCallback(() => {
    if (topSpreadAlertsSorted.length === 0) {
      void message.warning('No rows to export for this table.');
      return;
    }
    const matrix = buildSpreadAlertExportMatrix(topSpreadAlertsSorted, employmentType);
    const base = `teacher-equity_spread-alerts_${teacherEquityApiYear ?? 'year'}_${employmentType}`;
    const subtitles = [
      'Internal salary spread — priority schools',
      `${employmentLabel} · ${academicYearLabel || '—'} · Generated ${new Date().toLocaleString()}`,
      `${topSpreadAlertsSorted.length} schools (filters applied; complete list)`,
    ];
    void (async () => {
      try {
        await downloadStringMatrixAsPdf(base, subtitles, SPREAD_ALERT_EXPORT_HEADERS, matrix);
        void message.success(`Downloaded ${matrix.length} rows to PDF.`);
      } catch (e) {
        void message.error(e instanceof Error ? e.message : 'PDF export failed.');
      }
    })();
  }, [topSpreadAlertsSorted, employmentType, teacherEquityApiYear, employmentLabel, academicYearLabel]);

  const handleExportBelowPeerExcel = useCallback(() => {
    if (belowPeerAlertsSorted.length === 0) {
      void message.warning('No rows to export for this table.');
      return;
    }
    const matrix = buildBelowPeerExportMatrix(belowPeerAlertsSorted, employmentType);
    const base = `teacher-equity_below-peer_${teacherEquityApiYear ?? 'year'}_${employmentType}`;
    void (async () => {
      try {
        await downloadStringMatrixAsXlsx(base, 'Below peer median', BELOW_PEER_EXPORT_HEADERS, matrix);
        void message.success(`Downloaded ${matrix.length} rows to Excel.`);
      } catch (e) {
        void message.error(e instanceof Error ? e.message : 'Excel export failed.');
      }
    })();
  }, [belowPeerAlertsSorted, employmentType, teacherEquityApiYear]);

  const handleExportBelowPeerPdf = useCallback(() => {
    if (belowPeerAlertsSorted.length === 0) {
      void message.warning('No rows to export for this table.');
      return;
    }
    const matrix = buildBelowPeerExportMatrix(belowPeerAlertsSorted, employmentType);
    const base = `teacher-equity_below-peer_${teacherEquityApiYear ?? 'year'}_${employmentType}`;
    const subtitles = [
      'Below peer median — priority schools',
      `${employmentLabel} · ${academicYearLabel || '—'} · Generated ${new Date().toLocaleString()}`,
      `${belowPeerAlertsSorted.length} schools (filters applied; complete list)`,
    ];
    void (async () => {
      try {
        await downloadStringMatrixAsPdf(base, subtitles, BELOW_PEER_EXPORT_HEADERS, matrix, {
          arabicColumnIndices: [0, 2, 11],
        });
        void message.success(`Downloaded ${matrix.length} rows to PDF.`);
      } catch (e) {
        void message.error(e instanceof Error ? e.message : 'PDF export failed.');
      }
    })();
  }, [belowPeerAlertsSorted, employmentType, teacherEquityApiYear, employmentLabel, academicYearLabel]);

  const roleSelectOptions = useMemo(
    () => (rolesQuery.data ?? []).map((o) => ({ label: o.text, value: o.value })),
    [rolesQuery.data]
  );
  const qualSelectOptions = useMemo(
    () => (qualificationsQuery.data ?? []).map((o) => ({ label: o.text, value: o.value })),
    [qualificationsQuery.data]
  );

  const stickyTh = 'sticky left-0 z-[2] min-w-[11rem] border-r border-slate-200 bg-slate-100';
  /** Opaque backgrounds so peer-variance / other columns do not show through the sticky column on row hover. */
  const stickyTd =
    'sticky left-0 z-[2] min-w-[11rem] border-r border-slate-100 bg-white group-hover:bg-indigo-50';

  return (
    <div
      className={`min-h-screen bg-gradient-to-b pb-20 transition-[background] duration-500 ${
        employmentType === 'permanent'
          ? 'from-slate-50 via-slate-50/98 to-white'
          : 'from-stone-50/95 via-slate-50/90 to-white'
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 pt-8 sm:px-6 lg:px-8">
        <PageHeader
          title="National teacher equity report"
          description="Select the reporting year, optionally narrow by role and qualification, then press Search. Use the employment type control to switch between permanent and contractual views. Open a school row to view its teacher budget lines."
        />

        <div className="mt-5 space-y-4 rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5">
          <div>
            <label
              htmlFor="te-global-year"
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-700"
            >
              <GraduationCap className="size-4 text-indigo-600" aria-hidden />
              Reporting year <span className="text-red-600">*</span>
            </label>
            <select
              id="te-global-year"
              value={academicYearId}
              onChange={(e) => setAcademicYearId(e.target.value)}
              disabled={reportYearsQuery.isPending && budgetYearOptions.length === 0}
              className="mt-2 w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 sm:text-base"
            >
              <option value="">Select academic year…</option>
              {budgetYearOptions.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.label}
                </option>
              ))}
            </select>
            {reportYearsQuery.isError ? (
              <Alert
                type="error"
                showIcon
                className="mt-3"
                message="Could not load years"
                description={
                  reportYearsQuery.error instanceof Error
                    ? reportYearsQuery.error.message
                    : 'Could not load reporting years. Please try again later.'
                }
              />
            ) : null}
            {!reportYearsQuery.isPending && !reportYearsQuery.isError && budgetYearOptions.length === 0 ? (
              <p className="mt-2 text-xs font-medium text-amber-800">No reporting years returned for your account.</p>
            ) : null}
            {reportYearsQuery.isPending && !budgetYearOptions.length ? (
              <p className="mt-2 text-xs text-slate-500">Loading years…</p>
            ) : null}
          </div>

          <div
            className={`flex flex-col gap-3 rounded-lg border px-3 py-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between ${
              employmentType === 'permanent'
                ? 'border-slate-200/70 bg-slate-50/50'
                : 'border-stone-200/60 bg-stone-50/40'
            }`}
          >
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600">Employment type</span>
              <div className="mt-2">
                <EmploymentTypeSwitch value={employmentType} onChange={setEmploymentType} />
              </div>
            </div>
            <div className="min-w-[14rem] flex-1 sm:max-w-xs">
              <label className="text-[10px] font-bold uppercase tracking-wide text-slate-600">Roles</label>
              <Select
                mode="multiple"
                allowClear
                placeholder="All roles"
                className="mt-1.5 w-full"
                options={roleSelectOptions}
                value={draftRoleIds}
                onChange={(v) => setDraftRoleIds(v)}
                loading={rolesQuery.isPending}
                maxTagCount="responsive"
                optionFilterProp="label"
              />
            </div>
            <div className="min-w-[14rem] flex-1 sm:max-w-xs">
              <label className="text-[10px] font-bold uppercase tracking-wide text-slate-600">Qualifications</label>
              <Select
                mode="multiple"
                allowClear
                placeholder="All qualifications"
                className="mt-1.5 w-full"
                options={qualSelectOptions}
                value={draftQualificationIds}
                onChange={(v) => setDraftQualificationIds(v)}
                loading={qualificationsQuery.isPending}
                maxTagCount="responsive"
                optionFilterProp="label"
              />
            </div>
            <div className="flex shrink-0 items-end pb-0.5">
              <Button
                type="button"
                variant="primary"
                disabled={!yearSelected || nationalKpisFetching}
                onClick={applyNationalFilters}
                className="inline-flex items-center gap-2 !px-4 !py-2"
              >
                <Search className="size-4 shrink-0" aria-hidden />
                Search
              </Button>
            </div>
          </div>

          {filtersPendingApply ? (
            <p className="text-xs font-medium text-amber-800">
              Role and qualification choices are not applied yet — press <strong>Search</strong> to refresh the national
              figures and school tables.
            </p>
          ) : null}

          <p className="text-xs leading-relaxed text-slate-500">
            Role and qualification filters are applied when you press <strong>Search</strong>. Leaving both empty is treated
            as <span className="font-medium text-slate-700">all roles</span> and{' '}
            <span className="font-medium text-slate-700">all qualifications</span> for the national view.
          </p>
        </div>

        {yearSelected ? (
          nationalKpisQuery.isLoading ? (
            <TeacherEquityLoadingState yearLabel={academicYearLabel} />
          ) : (
            <>
              <section className="mt-6 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-md ring-1 ring-slate-100">
                <div className="flex flex-wrap items-start gap-3 border-b border-slate-100 pb-5">
                  <LayoutDashboard className="mt-0.5 size-8 shrink-0 text-indigo-600" aria-hidden />
                  <div>
                    <h2 className="text-xl font-bold tracking-tight text-slate-900">Executive summary</h2>
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                      National figures for <strong>{academicYearLabel}</strong> (reporting year{' '}
                      <strong>{teacherEquityApiYear ?? '—'}</strong>): overall median salary and average internal salary spread
                      for the employment type you selected. Role and qualification filters apply after you press{' '}
                      <strong>Search</strong>.
                    </p>
                  </div>
                </div>

                {nationalKpisQuery.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    className="mt-5"
                    message="Could not load national KPIs"
                    description={
                      nationalKpisQuery.error instanceof ApiError
                        ? ApiError.getUserMessage(nationalKpisQuery.error)
                        : nationalKpisQuery.error instanceof Error
                          ? nationalKpisQuery.error.message
                          : 'The national figures could not be loaded. Please try again later.'
                    }
                  />
                ) : null}

                {nationalKpisQuery.data &&
                nationalKpisQuery.data.overallMedianPermanent == null &&
                nationalKpisQuery.data.overallMedianContractual == null ? (
                  <Alert
                    type="info"
                    showIcon
                    className="mt-5"
                    message="National medians not available for this filter"
                    description="Overall median salary is not shown for this role and qualification combination—often because too few teachers match. Other national figures and school lists may still appear where values can be calculated for the employment type you selected."
                  />
                ) : null}

                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {employmentType === 'permanent' ? (
                    <>
                      <div className="rounded-xl border border-slate-300/70 bg-gradient-to-br from-white to-slate-50 p-5 shadow-sm ring-1 ring-slate-200/80 transition-shadow duration-300">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-600">
                          Overall median salary (Permanent)
                        </p>
                        <p className="mt-2 font-mono text-2xl font-bold text-slate-900">
                          {nationalKpisQuery.isPending
                            ? '…'
                            : nationalKpisQuery.data
                              ? fmtMoneyOrDash(nationalKpisQuery.data.overallMedianPermanent)
                              : '—'}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">National median — permanent teachers.</p>
                      </div>
                      <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/70 to-white p-5 shadow-sm ring-1 ring-emerald-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-900">
                          Overall average salary (Permanent)
                        </p>
                        <p className="mt-2 font-mono text-2xl font-bold text-emerald-950">
                          {nationalKpisQuery.isPending
                            ? '…'
                            : nationalKpisQuery.data
                              ? fmtMoneyOrDash(nationalKpisQuery.data.overallAveragePermanent)
                              : '—'}
                        </p>
                        <p className="mt-2 text-xs text-emerald-950/80">National average — permanent teachers.</p>
                      </div>
                      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-5 shadow-sm ring-1 ring-indigo-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-indigo-900">
                          Avg. internal salary spread (Permanent)
                        </p>
                        <p className="mt-2 font-mono text-2xl font-bold text-indigo-950">
                          {nationalKpisQuery.isPending
                            ? '…'
                            : nationalKpisQuery.data
                              ? fmtMoneyOrDash(nationalKpisQuery.data.avgInternalSpreadPermanent)
                              : '—'}
                        </p>
                        <p className="mt-2 text-xs text-indigo-950/80">National average internal spread — permanent teachers.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-xl border border-stone-300/60 bg-gradient-to-br from-white to-stone-50/80 p-5 shadow-sm ring-1 ring-stone-200/70 transition-shadow duration-300">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-600">
                          Overall median salary (Contractual)
                        </p>
                        <p className="mt-2 font-mono text-2xl font-bold text-slate-900">
                          {nationalKpisQuery.isPending
                            ? '…'
                            : nationalKpisQuery.data
                              ? fmtMoneyOrDash(nationalKpisQuery.data.overallMedianContractual)
                              : '—'}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">National median — contractual teachers.</p>
                      </div>
                      <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/70 to-white p-5 shadow-sm ring-1 ring-emerald-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-900">
                          Overall average salary (Contractual)
                        </p>
                        <p className="mt-2 font-mono text-2xl font-bold text-emerald-950">
                          {nationalKpisQuery.isPending
                            ? '…'
                            : nationalKpisQuery.data
                              ? fmtMoneyOrDash(nationalKpisQuery.data.overallAverageContractual)
                              : '—'}
                        </p>
                        <p className="mt-2 text-xs text-emerald-950/80">National average — contractual teachers.</p>
                      </div>
                      <div className="rounded-xl border border-stone-300/60 bg-gradient-to-br from-stone-50/90 to-white p-5 shadow-sm ring-1 ring-stone-200/70 transition-shadow duration-300">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-800">
                          Avg. internal salary spread (Contractual)
                        </p>
                        <p className="mt-2 font-mono text-2xl font-bold text-slate-900">
                          {nationalKpisQuery.isPending
                            ? '…'
                            : nationalKpisQuery.data
                              ? fmtMoneyOrDash(nationalKpisQuery.data.avgInternalSpreadContractual)
                              : '—'}
                        </p>
                        <p className="mt-2 text-xs text-slate-600">National average internal spread — contractual teachers.</p>
                      </div>
                    </>
                  )}
                </div>
              </section>

              <section className="mt-6 rounded-2xl border border-amber-200/80 bg-amber-50/30 p-6 shadow-md ring-1 ring-amber-100/80">
                <h2 className="text-sm font-bold uppercase tracking-wide text-amber-950">School priority lists</h2>
                <p className="mt-2 text-sm text-amber-950/90">
                  For <strong>{employmentLabel}</strong> and <strong>{academicYearLabel}</strong>, using the role and
                  qualification filters last applied with <strong>Search</strong>. Select a row to open that school&apos;s
                  teacher budget detail.
                </p>

                <div className="mt-8 space-y-10">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-800">
                      Widest internal salary spread (national)
                    </h3>
                    <p className="mt-1 text-xs text-slate-600">
                      Sort and filter below; {SCHOOL_ALERTS_PAGE_SIZE} schools per page. Spread and severity show a dash when
                      no value is available for the employment type you selected. Peer and band medians appear when present
                      in the source data.
                    </p>
                    <DashboardSettingSourceNotes
                      className="mt-2"
                      resolved={teacherEquitySpreadSettings}
                      singleLink
                      compactLabels={[
                        'very high ratio',
                        'high ratio',
                        'very high amount',
                        'high amount',
                      ]}
                    />
                    <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
                      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                        <div className="min-w-[12rem] flex-1 sm:max-w-xs">
                          <Input
                            placeholder="School name or educational center #…"
                            value={spreadAlertSearch}
                            onChange={(e) => setSpreadAlertSearch(e.target.value)}
                            aria-label="Filter spread table by school name or educational center number"
                          />
                        </div>
                        <div className="min-w-[10rem] sm:w-44">
                          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
                            District
                          </label>
                          <select
                            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-2 pr-8 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            value={spreadAlertDistrict}
                            onChange={(e) => setSpreadAlertDistrict(e.target.value)}
                            aria-label="Filter spread table by district"
                          >
                            <option value="">All districts</option>
                            {schoolSpreadDistrictOptions.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 lg:ml-auto">
                        <Button
                          type="button"
                          variant="secondary"
                          className="!gap-1.5 !px-3 !py-2 text-xs"
                          disabled={topSpreadAlertsSorted.length === 0}
                          onClick={handleExportSpreadExcel}
                        >
                          <FileSpreadsheet className="size-4 shrink-0" aria-hidden />
                          Excel
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!gap-1.5 !px-3 !py-2 text-xs"
                          disabled={topSpreadAlertsSorted.length === 0}
                          onClick={handleExportSpreadPdf}
                        >
                          <FileText className="size-4 shrink-0" aria-hidden />
                          PDF
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="w-full min-w-[1160px] border-collapse text-left text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                            <th className={`px-3 py-3 ${stickyTh}`}>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-left hover:text-indigo-700"
                                onClick={() => toggleSpreadAlertSort('schoolName')}
                              >
                                School name
                                {spreadAlertSortKey === 'schoolName' ? (
                                  spreadAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-bold normal-case tracking-normal text-slate-600">
                              Educational center #
                            </th>
                            <th className="px-3 py-3">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-left hover:text-indigo-700"
                                onClick={() => toggleSpreadAlertSort('district')}
                              >
                                District
                                {spreadAlertSortKey === 'district' ? (
                                  spreadAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleSpreadAlertSort('headcount')}
                              >
                                Teachers
                                {spreadAlertSortKey === 'headcount' ? (
                                  spreadAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleSpreadAlertSort('totalSalaryMedian')}
                              >
                                School average salary
                                {spreadAlertSortKey === 'totalSalaryMedian' ? (
                                  spreadAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleSpreadAlertSort('districtPeerMedian')}
                              >
                                District peer median
                                {spreadAlertSortKey === 'districtPeerMedian' ? (
                                  spreadAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">Size band median</th>
                            <th className="px-3 py-3 text-right">Tuition band median</th>
                            <th className="px-3 py-3 text-right">Parent tuition median</th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleSpreadAlertSort('spread')}
                              >
                                Spread
                                {spreadAlertSortKey === 'spread' ? (
                                  spreadAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3">Severity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topSpreadAlertsSorted.length === 0 ? (
                            <tr>
                              <td colSpan={11} className="px-3 py-8 text-center text-sm text-slate-500">
                                No schools match the current search or district filter.
                              </td>
                            </tr>
                          ) : (
                            topSpreadAlerts.map((row) => {
                              const spreadOk = row.spread != null && Number.isFinite(row.spread);
                              const sev = severityStyles(row.severity);
                              const widthPct = row.severity === 'very_high' ? 100 : row.severity === 'high' ? 66 : 40;
                              const src = row.source;
                              const enrollMed = cohortSchoolBandMedianTotalSalary(src, employmentType);
                              const tuitionMed = cohortTuitionBandMedianTotalSalary(src, employmentType);
                              const parentMed = cohortParentTuitionBandMedianTotalSalary(src, employmentType);
                              const medSal = cohortTotalSalaryMedian(src, employmentType);
                              const distMed = cohortDistrictMedian(src, employmentType);
                              return (
                                <tr
                                  key={row.schoolId}
                                  className="group cursor-pointer border-b border-slate-100 transition hover:bg-indigo-50"
                                  onClick={() =>
                                    openSchoolDrawer(row.schoolId, row.schoolName, row.districtLabel, row.source.budgetId)
                                  }
                                >
                                  <td className={`px-3 py-2.5 ${stickyTd}`}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSchoolDrawer(
                                          row.schoolId,
                                          row.schoolName,
                                          row.districtLabel,
                                          row.source.budgetId
                                        )
                                      }}
                                      className="text-left font-semibold text-indigo-700 underline decoration-indigo-300 decoration-1 underline-offset-2 hover:text-indigo-900"
                                    >
                                      {row.schoolName}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2.5 font-mono text-sm tabular-nums text-slate-800">
                                    {src.educationalCenterNumber ?? '—'}
                                  </td>
                                  <td className="max-w-[12rem] truncate px-3 py-2.5 text-slate-700" title={row.districtLabel}>
                                    {row.districtLabel}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">
                                    {cohortHeadcount(src, employmentType)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">
                                    {medSal != null ? fmtMoney(medSal) : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                                    {distMed != null ? fmtMoney(distMed) : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                                    {enrollMed != null ? fmtMoney(enrollMed) : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                                    {tuitionMed != null ? fmtMoney(tuitionMed) : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                                    {parentMed != null ? fmtMoney(parentMed) : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono font-semibold tabular-nums text-slate-900">
                                    {fmtMoneyOrDash(row.spread)}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    {spreadOk ? (
                                      <div className="flex items-center gap-2">
                                        <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-200">
                                          <div
                                            className={`h-full rounded-full ${row.barColorHex ? '' : sev.bar}`}
                                            style={{
                                              width: `${widthPct}%`,
                                              ...(row.barColorHex ? { backgroundColor: row.barColorHex } : {}),
                                            }}
                                          />
                                        </div>
                                        <span className={`text-xs font-semibold ${sev.text}`}>{row.severityLabel}</span>
                                      </div>
                                    ) : (
                                      <span className="text-xs font-medium text-slate-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                      <span>
                        {topSpreadAlertsSorted.length === 0
                          ? '—'
                          : `${spreadAlertPage * SCHOOL_ALERTS_PAGE_SIZE + 1}–${Math.min(
                              (spreadAlertPage + 1) * SCHOOL_ALERTS_PAGE_SIZE,
                              topSpreadAlertsSorted.length
                            )} of ${topSpreadAlertsSorted.length}`}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-slate-500">
                          Page {spreadAlertPage + 1} / {spreadAlertPageCount}
                        </span>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!px-3 !py-1.5 text-xs"
                          disabled={spreadAlertPage <= 0}
                          onClick={() => setSpreadAlertPage((p) => Math.max(0, p - 1))}
                        >
                          Previous
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!px-3 !py-1.5 text-xs"
                          disabled={spreadAlertPage >= spreadAlertPageCount - 1}
                          onClick={() => setSpreadAlertPage((p) => Math.min(spreadAlertPageCount - 1, p + 1))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-800">
                      Furthest below peer median (national)
                    </h3>
                    <p className="mt-1 text-xs text-slate-600">
                      Same national list as above, filtered here independently. Average peer variance and comparison columns
                      show a dash when no value is available for the employment type you selected.
                    </p>
                    <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
                      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                        <div className="min-w-[12rem] flex-1 sm:max-w-xs">
                          <Input
                            placeholder="School name or educational center #…"
                            value={varianceAlertSearch}
                            onChange={(e) => setVarianceAlertSearch(e.target.value)}
                            aria-label="Filter variance table by school name or educational center number"
                          />
                        </div>
                        <div className="min-w-[10rem] sm:w-44">
                          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
                            District
                          </label>
                          <select
                            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-2 pr-8 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            value={varianceAlertDistrict}
                            onChange={(e) => setVarianceAlertDistrict(e.target.value)}
                            aria-label="Filter variance table by district"
                          >
                            <option value="">All districts</option>
                            {schoolSpreadDistrictOptions.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 lg:ml-auto">
                        <Button
                          type="button"
                          variant="secondary"
                          className="!gap-1.5 !px-3 !py-2 text-xs"
                          disabled={belowPeerAlertsSorted.length === 0}
                          onClick={handleExportBelowPeerExcel}
                        >
                          <FileSpreadsheet className="size-4 shrink-0" aria-hidden />
                          Excel
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!gap-1.5 !px-3 !py-2 text-xs"
                          disabled={belowPeerAlertsSorted.length === 0}
                          onClick={handleExportBelowPeerPdf}
                        >
                          <FileText className="size-4 shrink-0" aria-hidden />
                          PDF
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="w-full min-w-[1140px] border-collapse text-left text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                            <th className={`px-3 py-3 ${stickyTh}`}>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-left hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('schoolName')}
                              >
                                School name
                                {varianceAlertSortKey === 'schoolName' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-bold normal-case tracking-normal text-slate-600">
                              Educational center #
                            </th>
                            <th className="px-3 py-3">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-left hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('district')}
                              >
                                District
                                {varianceAlertSortKey === 'district' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('headcount')}
                              >
                                Teachers
                                {varianceAlertSortKey === 'headcount' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('avgPeerVariance')}
                              >
                                Avg. peer variance
                                {varianceAlertSortKey === 'avgPeerVariance' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-bold normal-case">School average salary</th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('districtVar')}
                              >
                                Dist. var %
                                {varianceAlertSortKey === 'districtVar' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('enrollmentVar')}
                              >
                                Size band var %
                                {varianceAlertSortKey === 'enrollmentVar' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('tuitionVar')}
                              >
                                Tuition var %
                                {varianceAlertSortKey === 'tuitionVar' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-right">
                              <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 hover:text-indigo-700"
                                onClick={() => toggleVarianceAlertSort('parentVar')}
                              >
                                Parent var %
                                {varianceAlertSortKey === 'parentVar' ? (
                                  varianceAlertSortDir === 'asc' ? (
                                    <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                                  )
                                ) : null}
                              </button>
                            </th>
                            <th
                              className="px-3 py-3 text-xs font-bold normal-case"
                              title={PEER_VARIANCE_DIMENSIONS_LABEL}
                            >
                              Compared against
                            </th>
                            <th className="px-3 py-3">Largest reported gap</th>
                          </tr>
                        </thead>
                        <tbody>
                          {belowPeerAlertsSorted.length === 0 ? (
                            <tr>
                              <td colSpan={12} className="px-3 py-8 text-center text-sm text-slate-500">
                                No schools match the current search or district filter.
                              </td>
                            </tr>
                          ) : (
                            belowPeerAlerts.map((row) => {
                              const src = row.source;
                              const dv = cohortDistrictVar(src, employmentType);
                              const ev = cohortSchoolBandVar(src, employmentType);
                              const tv = cohortTuitionBandVar(src, employmentType);
                              const pv = cohortParentTuitionBandVar(src, employmentType);
                              const medSal = cohortTotalSalaryMedian(src, employmentType);
                              const worst = resolvedWorstComparisonForCohort(src, employmentType);
                              const avgDisp = peerVariancePercentDisplayValue(medSal, row.avgPeerVariancePct);
                              return (
                                <tr
                                  key={row.schoolId}
                                  className="group cursor-pointer border-b border-slate-100 transition hover:bg-indigo-50"
                                  onClick={() =>
                                    openSchoolDrawer(row.schoolId, row.schoolName, row.districtLabel, row.source.budgetId)
                                  }
                                >
                                  <td className={`px-3 py-2.5 ${stickyTd}`}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSchoolDrawer(
                                          row.schoolId,
                                          row.schoolName,
                                          row.districtLabel,
                                          row.source.budgetId
                                        )
                                      }}
                                      className="text-left font-semibold text-indigo-700 underline decoration-indigo-300 decoration-1 underline-offset-2 hover:text-indigo-900"
                                    >
                                      {row.schoolName}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2.5 font-mono text-sm tabular-nums text-slate-800">
                                    {src.educationalCenterNumber ?? '—'}
                                  </td>
                                  <td className="max-w-[12rem] truncate px-3 py-2.5 text-slate-700" title={row.districtLabel}>
                                    {row.districtLabel}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">
                                    {cohortHeadcount(src, employmentType)}
                                  </td>
                                  <td
                                    className={`px-3 py-2.5 text-right font-mono text-sm font-semibold tabular-nums ${
                                      avgDisp != null && avgDisp < -5
                                        ? 'text-rose-700'
                                        : avgDisp != null && avgDisp < 0
                                          ? 'text-amber-800'
                                          : 'text-slate-800'
                                    }`}
                                  >
                                    {fmtPctSigned(avgDisp)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">
                                    {medSal != null ? fmtMoney(medSal) : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
                                    {fmtPctSigned(peerVariancePercentDisplayValue(medSal, dv))}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
                                    {fmtPctSigned(peerVariancePercentDisplayValue(medSal, ev))}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
                                    {fmtPctSigned(peerVariancePercentDisplayValue(medSal, tv))}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
                                    {fmtPctSigned(peerVariancePercentDisplayValue(medSal, pv))}
                                  </td>
                                  <td className="px-3 py-2.5 text-xs text-slate-600">{PEER_VARIANCE_DIMENSIONS_LABEL}</td>
                                  <td
                                    className="max-w-[10rem] truncate px-3 py-2.5 text-xs text-slate-800"
                                    title={worst ? worst : undefined}
                                  >
                                    {worst ?? '—'}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                      <span>
                        {belowPeerAlertsSorted.length === 0
                          ? '—'
                          : `${varianceAlertPage * SCHOOL_ALERTS_PAGE_SIZE + 1}–${Math.min(
                              (varianceAlertPage + 1) * SCHOOL_ALERTS_PAGE_SIZE,
                              belowPeerAlertsSorted.length
                            )} of ${belowPeerAlertsSorted.length}`}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-slate-500">
                          Page {varianceAlertPage + 1} / {varianceAlertPageCount}
                        </span>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!px-3 !py-1.5 text-xs"
                          disabled={varianceAlertPage <= 0}
                          onClick={() => setVarianceAlertPage((p) => Math.max(0, p - 1))}
                        >
                          Previous
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!px-3 !py-1.5 text-xs"
                          disabled={varianceAlertPage >= varianceAlertPageCount - 1}
                          onClick={() => setVarianceAlertPage((p) => Math.min(varianceAlertPageCount - 1, p + 1))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )
        ) : (
          <TeacherEquityYearLanding />
        )}
      </div>

      {drawerOpen && drawerSchool && teacherEquityApiYear != null ? (
        <SchoolTeachersDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setDrawerSchool(null);
          }}
          schoolName={drawerSchool.name}
          districtLabel={drawerSchool.district}
          budgetId={drawerSchool.budgetId}
          year={teacherEquityApiYear}
          employmentType={employmentType}
          appliedRoleIds={appliedCohortFilters.roleIds}
          appliedQualificationIds={appliedCohortFilters.qualificationIds}
        />
      ) : null}
    </div>
  );
}
